import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { ADMIN_API_URL_SUBPATH } from '@/constants';
import { axiosInstance, CreateEntityResponse, LanguageRequired, TAG, Tag } from '@/core';

type CreateTagData = {
  name: LanguageRequired;
  image_id?: number;
  is_active: boolean;
};

export const useTag = (config?: {
  onSuccess?: (data: AxiosResponse<CreateEntityResponse>) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryConfig = {
    url: `${ADMIN_API_URL_SUBPATH}/${TAG}`,
  };

  const createMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse>,
    unknown,
    CreateTagData,
    unknown
  > = useMutation({
    mutationKey: ['createTag'],
    mutationFn: (data: CreateTagData) => axiosInstance.post(queryConfig.url, data),
    onError: (error) => {
      config?.onError?.(error);
    },
    onSuccess: (data) => {
      config?.onSuccess?.(data);
    },
  });

  const useTagQuery = (id: number) =>
    useQuery<AxiosResponse<Tag>>({
      queryKey: ['tag', id],
      queryFn: () => axiosInstance.get(`${queryConfig.url}/${id}`),
      enabled: !!id,
    });

  const updateMutation = useMutation({
    mutationKey: ['updateTag'],
    mutationFn: (data: Tag) => axiosInstance.put(`${queryConfig.url}/${data.id}`, data),
  });

  const deleteMutation = useMutation({
    mutationKey: ['deleteTag'],
    mutationFn: (id: number) => axiosInstance.delete(`${queryConfig.url}/${id}`),
  });

  const duplicateMutation = useMutation({
    mutationKey: ['duplicateTag'],
    mutationFn: (id: number) => axiosInstance.post(`${queryConfig.url}/${id}/duplicate`),
  });

  const deactivateMutation = useMutation({
    mutationKey: ['deactivateTag'],
    mutationFn: (id: number) => axiosInstance.put(`${queryConfig.url}/${id}`, { is_active: false }),
  });

  const activateMutation = useMutation({
    mutationKey: ['activateTag'],
    mutationFn: (id: number) => axiosInstance.put(`${queryConfig.url}/${id}`, { is_active: true }),
  });

  return {
    mutations: {
      createMutation,
      updateMutation,
      deleteMutation,
      duplicateMutation,
      deactivateMutation,
      activateMutation,
    },
    queries: {
      useTagQuery,
    },
  };
};
